import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Footprints, Search, History, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

interface CareerPathPlayer {
  id: number;
  name: string;
  photo: string | null;
  position: string;
  isActive: boolean;
  nation: { code: string } | null;
  club: { name: string } | null;
  _count: { careerEntries: number };
}

const PAGE_SIZE = 15;

export default function CareerPathPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<CareerPathPlayer[]>({
    queryKey: ["quiz", "career-path-players"],
    queryFn: async () => {
      const r = await apiClient.get(API_ENDPOINTS.QUIZ.CAREER_PATH_PLAYERS) as unknown as CareerPathPlayer[] | null;
      return r ?? [];
    },
  });
  const players = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quiz", "career-path-players"] });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      isActive
        ? apiClient.patch(API_ENDPOINTS.PLAYERS.ACTIVATE(id), {})
        : apiClient.patch(API_ENDPOINTS.PLAYERS.DEACTIVATE(id), {}),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const filtered = players.filter((p) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(s) ||
      p.club?.name?.toLowerCase().includes(s) ||
      p.nation?.code?.toLowerCase().includes(s) ||
      p.position?.toLowerCase().includes(s)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Footprints className="h-8 w-8 text-violet-500" />
            Career Path
          </h1>
          <p className="text-gray-500 mt-1">
            {players.length} players set up · career entries managed via the Players section
          </p>
        </div>
        <Button
          onClick={() => navigate("/players")}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          <History className="h-4 w-4 mr-2" />
          Manage in Players
        </Button>
      </div>

      {/* Info */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-5 text-sm text-violet-800">
        Career Path uses each player's career timeline. To add or edit a player's career entries,
        go to <strong>Players</strong> and click the <strong>History</strong> icon next to a player.
      </div>

      {/* Search */}
      <div className="relative max-w-xs mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, club, position…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Footprints className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">
            {search ? "No matches" : "No players with career entries yet"}
          </p>
          {!search && (
            <p className="text-sm mt-1">
              Go to Players → History to add career entries for a player.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-2.5 font-medium">Player</th>
                <th className="text-left px-4 py-2.5 font-medium">Nat</th>
                <th className="text-left px-4 py-2.5 font-medium">Club</th>
                <th className="text-left px-4 py-2.5 font-medium">Pos</th>
                <th className="text-left px-4 py-2.5 font-medium">Career Clubs</th>
                <th className="text-left px-4 py-2.5 font-medium">Active</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paged.map((p) => (
                <tr
                  key={p.id}
                  className={cn("hover:bg-gray-50 transition-colors", !p.isActive && "opacity-50")}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {p.photo ? (
                        <img src={p.photo} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-100 shrink-0" />
                      )}
                      <span className="font-medium text-gray-900">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">{p.nation?.code ?? "—"}</td>
                  <td className="px-4 py-2.5 text-gray-500 max-w-[130px] truncate">{p.club?.name ?? "—"}</td>
                  <td className="px-4 py-2.5 text-gray-500">{p.position}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
                      {p._count.careerEntries} clubs
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <Switch
                      checked={p.isActive}
                      onCheckedChange={(v) => toggle({ id: p.id, isActive: v })}
                      className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400"
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-violet-600"
                      onClick={() => navigate(`/players/${p.id}/career`)}
                    >
                      <History className="h-3.5 w-3.5 mr-1" />
                      Edit career
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
          <span>
            {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
              .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                acc.push(n);
                return acc;
              }, [])
              .map((n, i) =>
                n === "…" ? (
                  <span key={`e-${i}`} className="px-1">…</span>
                ) : (
                  <Button
                    key={n}
                    variant={n === safePage ? "default" : "outline"}
                    size="sm"
                    className={cn("h-8 w-8 p-0", n === safePage && "bg-violet-600 hover:bg-violet-700 text-white")}
                    onClick={() => setPage(n as number)}
                  >
                    {n}
                  </Button>
                )
              )}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
